import React, { FC, useContext, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FormAccessManagementModal } from 'src/feature/FormAccessManagementModal';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import { useGetForm } from 'src/shared/lib/hooks/useGetForm';
import { canAccessGroups, canManageModel } from 'src/shared/store/user/permissions';
import { FormType } from 'src/shared/store/projects/types';
import { UserContext } from 'src/shared/store/user';
import { useClickOutside } from 'src/shared/lib/hooks/useClickOutside';
import { Button } from 'src/shared/ui';
import { Loader } from 'src/shared/ui/Loader';

interface FormsListProps {
  isSelected: boolean;
  search: string;
  handleOpenFormModal: () => void;
}

interface FormAccessActionsProps {
  form: FormType;
  onOpenAccessModal: (form: FormType) => void;
}

const FormAccessActions: FC<FormAccessActionsProps> = ({ form, onOpenAccessModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useClickOutside(actionsRef, () => {
    setIsMenuOpen(false);
  });

  return (
    <div className="relative ml-auto flex-shrink-0" ref={actionsRef}>
      <button
        type="button"
        className="flex h-7 w-7 items-center justify-center rounded text-lg leading-none text-black transition-all hover:bg-violet-300 dark:text-white dark:hover:bg-violet-1200"
        onClick={event => {
          event.stopPropagation();
          setIsMenuOpen(currentState => !currentState);
        }}
      >
        ⋮
      </button>

      {isMenuOpen && (
        <div className="absolute right-0 top-8 z-20 min-w-52 rounded border border-violet-400 bg-white-100 p-1 shadow-white-xl dark:border-violet-900 dark:bg-violet-1100">
          <button
            type="button"
            className="font-gothampro-400 flex w-full rounded px-3 py-2 text-left text-sm text-black transition-all hover:bg-violet-300 dark:text-white dark:hover:bg-violet-1200"
            onClick={event => {
              event.stopPropagation();
              onOpenAccessModal(form);
              setIsMenuOpen(false);
            }}
          >
            Настроить права доступа
          </button>
        </div>
      )}
    </div>
  );
};

export const FormsList: FC<FormsListProps> = ({ search, isSelected, handleOpenFormModal }) => {
  const { forms, isFormsLoading, selectedForm } = useContext(ProjectsContext);
  const { user, darkMode } = useContext(UserContext);
  const [accessModalForm, setAccessModalForm] = useState<FormType | null>(null);

  const { handleSelectForm } = useGetForm();

  const filteredForms = useMemo(() => {
    const normalizedSearch = search.trim();
    const isAdvancedSearch =
      /(^|\s)(field:|attr:|content:|cube:|status:|dimension:|dim:|created[<>]=|updated[<>]=)/i.test(
        normalizedSearch,
      );

    if (!normalizedSearch || isAdvancedSearch) {
      return forms;
    }

    return forms.filter(({ form_name }) =>
      form_name.toLowerCase().includes(normalizedSearch.toLowerCase()),
    );
  }, [search, forms]);

  const canManageAccess = canAccessGroups(user);

  return (
    <div className="mb-4">
      {isSelected && isFormsLoading && (
        <div className="flex h-28 w-full items-center justify-center">
          <Loader classNames="w-10 h-10" />
        </div>
      )}

      {isSelected && !isFormsLoading && (
        <div className="w-full">
          {!filteredForms?.length && (
            <div className={'flex h-14 min-h-14 w-full items-center justify-center'}>
              <div className="font-gothampro-400 tex-sm dark:text-white">Формы не найдены</div>
            </div>
          )}

          {filteredForms.map(form => (
            <div
              key={`form-${form.id}`}
              className={cn(
                {
                  'opacity-100': form.id === selectedForm?.id,
                  'opacity-50 hover:opacity-100': form.id !== selectedForm?.id,
                },
                'ml-5 flex h-10 min-h-10 w-64 cursor-pointer items-center gap-2',
              )}
              onClick={() => handleSelectForm(form)}
            >
              <div className="font-gothampro-400 tex-sm min-w-0 flex-1 truncate text-black dark:text-white">
                {form.form_name}
              </div>
              {canManageAccess && (
                <FormAccessActions form={form} onOpenAccessModal={setAccessModalForm} />
              )}
            </div>
          ))}

          {canManageModel(user) && (
            <div className="pr-1">
              <Button
                variant={darkMode ? 'violetDark' : 'violetOutline'}
                onClick={handleOpenFormModal}
              >
                Добавить форму
              </Button>
            </div>
          )}
        </div>
      )}

      {accessModalForm && (
        <FormAccessManagementModal
          tableId={accessModalForm.id}
          formLabel={accessModalForm.form_name}
          onClose={() => setAccessModalForm(null)}
        />
      )}
    </div>
  );
};
